import { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTimes,
  faEnvelope,
  faComputer,
  faShieldAlt,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons'
import { Button, Input } from '../shared/components/ui'
import type { GuestAccessLevel } from '../types/guest'

interface Device {
  id: number
  name: string
  wisolId?: string
}

interface GuestInvitationDialogProps {
  open: boolean
  onClose: () => void
  onInvite: (email: string, deviceIds: number[], accessLevel: GuestAccessLevel) => Promise<void>
  devices: Device[]
  existingGuestEmails?: string[]
}

export default function GuestInvitationDialog({
  open,
  onClose,
  onInvite,
  devices,
  existingGuestEmails = [],
}: GuestInvitationDialogProps) {
  const [email, setEmail] = useState('')
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([])
  const [emailError, setEmailError] = useState<string | null>(null)
  const [deviceError, setDeviceError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeviceListExpanded, setIsDeviceListExpanded] = useState(false)

  // Track previous open state to detect when dialog opens
  const wasOpen = useRef(false)
  // Track if submission is in flight (ref to avoid stale closure issues)
  const submissionInFlight = useRef(false)

  // Reset form only when dialog OPENS (not when devices change during submission)
  useEffect(() => {
    // Only reset when transitioning from closed to open
    if (open && !wasOpen.current && !submissionInFlight.current) {
      setEmail('')
      setSelectedDeviceIds(devices.map((d) => d.id))
      setEmailError(null)
      setDeviceError(null)
      setIsDeviceListExpanded(false)
    }
    wasOpen.current = open
  }, [open, devices])

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Guard against duplicate submissions
    if (isSubmitting || submissionInFlight.current) {
      console.warn('⚠️ Submission already in progress, ignoring duplicate')
      return
    }

    // Reset errors
    setEmailError(null)
    setDeviceError(null)

    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }

    if (!validateEmail(email.trim())) {
      setEmailError('Please enter a valid email address')
      return
    }

    // Check if guest is already invited by this user
    if (existingGuestEmails.some((e) => e.toLowerCase() === email.trim().toLowerCase())) {
      setEmailError('This guest has already been invited. You can manage their device access from the list below.')
      return
    }

    // Validate device selection
    if (selectedDeviceIds.length === 0) {
      setDeviceError('Please select at least one device')
      return
    }

    // Submit invitation
    try {
      setIsSubmitting(true)
      submissionInFlight.current = true
      await onInvite(email.trim(), selectedDeviceIds, 'VIEW_ONLY')
      onClose() // Close dialog on success
    } catch (error: any) {
      console.error('Failed to invite guest:', error)
      // Error is handled by parent component
    } finally {
      setIsSubmitting(false)
      submissionInFlight.current = false
    }
  }

  const toggleDeviceSelection = (deviceId: number) => {
    setDeviceError(null) // Clear error when user makes selection
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    )
  }

  const toggleAllDevices = () => {
    setDeviceError(null)
    if (selectedDeviceIds.length === devices.length) {
      setSelectedDeviceIds([])
    } else {
      setSelectedDeviceIds(devices.map((d) => d.id))
    }
  }

  const allSelected = devices.length > 0 && selectedDeviceIds.length === devices.length
  const someSelected = selectedDeviceIds.length > 0 && selectedDeviceIds.length < devices.length

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Invite Guest to Access Devices</h2>
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close dialog"
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email Input */}
          <div>
            <label htmlFor="guest-email" className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2 h-4 w-4 text-gray-400" />
              Guest Email *
            </label>
            <Input
              id="guest-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setEmailError(null)
              }}
              placeholder="guest@example.com"
              error={!!emailError}
              disabled={isSubmitting}
              className="w-full"
              autoFocus
            />
            {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
            <p className="mt-1 text-xs text-gray-500">An invitation email will be sent to this address</p>
          </div>

          {/* Device Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faComputer} className="mr-2 h-4 w-4 text-gray-400" />
              Select Devices *
            </label>
            <div className="border border-gray-300 rounded-md">
              {devices.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 italic">No devices available</p>
              ) : (
                <div>
                  {/* Select All Header with Expand/Collapse */}
                  <div
                    className={`flex items-center p-3 transition-colors ${
                      allSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    } ${isSubmitting ? 'opacity-50' : ''}`}
                  >
                    <label
                      className={`flex items-center flex-1 cursor-pointer ${isSubmitting ? 'cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected
                        }}
                        onChange={toggleAllDevices}
                        disabled={isSubmitting}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-700">
                        All Devices
                        <span className="ml-2 text-xs text-gray-500">
                          ({selectedDeviceIds.length}/{devices.length} selected)
                        </span>
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsDeviceListExpanded(!isDeviceListExpanded)}
                      disabled={isSubmitting}
                      className="ml-2 p-1 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                      aria-label={isDeviceListExpanded ? 'Collapse device list' : 'Expand device list'}
                    >
                      <FontAwesomeIcon icon={isDeviceListExpanded ? faChevronUp : faChevronDown} className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Collapsible Device List */}
                  {isDeviceListExpanded && (
                    <div className="border-t border-gray-200 p-2 space-y-1 max-h-48 overflow-y-auto">
                      {devices.map((device) => (
                        <label
                          key={device.id}
                          className={`flex items-center p-3 rounded-md cursor-pointer transition-colors ${
                            selectedDeviceIds.includes(device.id)
                              ? 'bg-blue-50 border border-blue-200'
                              : 'hover:bg-gray-50 border border-transparent'
                          } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDeviceIds.includes(device.id)}
                            onChange={() => toggleDeviceSelection(device.id)}
                            disabled={isSubmitting}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {device.name}
                            {device.wisolId && <span className="ml-2 text-xs text-gray-500">({device.wisolId})</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {deviceError && <p className="mt-1 text-sm text-red-600">{deviceError}</p>}
          </div>

          {/* Access Level Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FontAwesomeIcon icon={faShieldAlt} className="mr-2 h-4 w-4 text-gray-400" />
              Access Level
            </label>
            <div className="p-3 rounded-md border border-blue-300 bg-blue-50">
              <div className="text-sm font-medium text-gray-900">View Only</div>
              <p className="mt-1 text-xs text-gray-500">Guest can view device data and readings</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
              {isSubmitting ? 'Sending Invitation...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
